import { Button, Space, Tag } from "antd";
import { RightOutlined, DownOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import { FrontDeskBooking } from "../../types";
import { displayConfirmationNumber } from "../../utils/groupReference";
import { hasAnyPermission } from "../../utils/permissions";

// Backend POST /operations/billing/payments accepts billing:edit OR
// bookings:edit, so the Collect Deposit button mirrors that pair.
const canCollectDeposit = (): boolean => hasAnyPermission("billing:edit", "bookings:edit");

interface BookingColumnHandlers {
  handlePrintReceipt: (id: string, payload: any) => void;
  handleOpenAllocate: (booking: FrontDeskBooking) => void;
  handleOpenEdit: (booking: FrontDeskBooking) => void;
  handleOpenCheckIn: (booking: FrontDeskBooking) => void;
  handleOpenCheckOut: (booking: FrontDeskBooking) => void;
  handleDeleteBooking: (booking: FrontDeskBooking) => void;
  handleOpenView: (
    booking: FrontDeskBooking & { is_group_parent?: boolean; is_group_child?: boolean },
  ) => void;
  handleExtendStay?: (booking: FrontDeskBooking) => void;
  handleChangeRoom?: (booking: FrontDeskBooking) => void;
  handleCollectDeposit?: (booking: FrontDeskBooking) => void;
  handleNoShow?: (booking: FrontDeskBooking) => void;
}

export const getBookingColumns = (handlers: BookingColumnHandlers) => [
  {
    title: "Confirmation",
    dataIndex: "confirmation_number",
    key: "confirmation_number",
    width: 150,
    render: (v: string) => v ? displayConfirmationNumber(v) : "N/A",
  },
  {
    title: "Guest",
    key: "guest",
    width: 170,
    render: (_: any, row: FrontDeskBooking) => (
      <div>
        <strong>{row.guest_name}</strong>
        <div style={{ fontSize: "11px", color: "#666" }}>
          Guests: {row.number_of_guests}
        </div>
        {row.extra_beds ? (
          <div
            style={{ fontSize: "11px", color: "#b7791f", fontWeight: "bold" }}
          >
            Extra Beds: {row.extra_beds}
          </div>
        ) : null}
      </div>
    ),
  },
  {
    title: "Contact",
    key: "contact",
    width: 220,
    render: (_: any, row: FrontDeskBooking) => (
      <span>
        {row.email}
        <br />
        {row.phone}
      </span>
    ),
  },
  {
    title: "Stay",
    key: "stay",
    width: 170,
    render: (_: any, row: FrontDeskBooking) => (
      <span>
        {row.check_in_date
          ? dayjs(row.check_in_date).format("MMM D, YYYY")
          : "N/A"}{" "}
        to{" "}
        {row.check_out_date
          ? dayjs(row.check_out_date).format("MMM D, YYYY")
          : "N/A"}
        {((row as any).checked_in_at || (row as any).checked_out_at) && (
          <div style={{ fontSize: "11px", color: "#666" }}>
            {(row as any).checked_in_at &&
              `In ${dayjs((row as any).checked_in_at).format("HH:mm")}`}
            {(row as any).checked_in_at && (row as any).checked_out_at && " · "}
            {(row as any).checked_out_at &&
              `Out ${dayjs((row as any).checked_out_at).format("HH:mm")}`}
          </div>
        )}
      </span>
    ),
  },
  {
    title: "Room",
    key: "room",
    width: 140,
    render: (_: any, row: FrontDeskBooking) => (
      <span>{row.room_number || "Not Allocated"}</span>
    ),
  },
  {
    title: "Source",
    dataIndex: "reservation_source",
    key: "reservation_source",
    width: 130,
    render: (v: string) => {
      const sourceColors: Record<string, string> = {
        walk_in: "gold",
        direct: "green",
        website: "blue",
        booking_com: "orange",
        agoda: "red",
        expedia: "cyan",
        travel_agent: "purple",
        phone: "magenta",
        whatsapp: "lime",
        other: "default",
      };
      const sourceLabels: Record<string, string> = {
        walk_in: "Walk-In",
        direct: "Direct",
        website: "Website",
        booking_com: "Booking.com",
        agoda: "Agoda",
        expedia: "Expedia",
        travel_agent: "Travel Agent",
        phone: "Phone",
        whatsapp: "WhatsApp",
        other: "Other",
      };
      return (
        <Tag color={sourceColors[v] || "default"}>
          {sourceLabels[v] || v}
        </Tag>
      );
    },
  },
  {
    title: "Package",
    key: "package",
    width: 150,
    render: (_: any, row: FrontDeskBooking) => {
      const name = row.package_name || row.package_code;
      if (!name) return <span style={{ color: "#999" }}>—</span>;
      return <Tag color="cyan">{name}</Tag>;
    },
  },
  {
    title: "AC Status",
    dataIndex: "ac_status",
    key: "ac_status",
    width: 100,
    render: (v: string) => {
      let text = "Both";
      if (v === "ac") text = "AC";
      if (v === "non_ac") text = "Non-AC";
      return <Tag color={v === "ac" ? "cyan" : v === "non_ac" ? "volcano" : "default"}>{text}</Tag>;
    },
  },
  {
    title: "Status",
    dataIndex: "status",
    key: "status",
    width: 120,
    render: (v: string) => {
      const color =
        v === "checked_in"
          ? "green"
          : v === "checked_out"
            ? "blue"
            : v === "confirmed"
              ? "cyan"
              : v === "cancelled"
                ? "red"
                : v === "reserved"
                  ? "purple"
                  : v === "blocked"
                    ? "volcano"
                    : v === "no_show"
                      ? "magenta"
                      : "orange";
      return <Tag color={color}>{v}</Tag>;
    },
  },
  {
    title: "Actions",
    dataIndex: "actions",
    key: "actions",
    width: 320,
    render: (_: any, row: FrontDeskBooking & { is_group_parent?: boolean; is_group_child?: boolean }) => {
      // For group parents, show Collect Deposit + View Group Booking
      if (row.is_group_parent) {
        return (
          <Space wrap size="small">
            {handlers.handleCollectDeposit &&
              canCollectDeposit() &&
              row.status !== "cancelled" && row.status !== "checked_out" &&
              Number((row as any).deposit_amount || 0) > 0 &&
              Number((row as any).deposit_paid || 0) <
                Number((row as any).deposit_amount || 0) && (
                <Button
                  size="small"
                  style={{
                    backgroundColor: "#fa8c16",
                    color: "#fff",
                    borderColor: "#fa8c16",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleCollectDeposit!(row);
                  }}
                >
                  Collect Deposit
                </Button>
            )}
            <Button
              size="small"
              type="primary"
              icon={<RightOutlined />}
              onClick={(e) => {
                e.stopPropagation();
                handlers.handleOpenView(row as any);
              }}
            >
              View Group
            </Button>
          </Space>
        );
      }

      const isAllocated = !!row.room_id || !!row.room_number;

      return (
        <Space direction="vertical" size="small" align="start">
          <Space wrap>
            {handlers.handleCollectDeposit &&
              canCollectDeposit() &&
              !row.is_group_child &&
              (row.status === "pending" ||
                row.status === "confirmed" ||
                row.status === "reserved") &&
              Number((row as any).deposit_amount || 0) > 0 &&
              Number((row as any).deposit_paid || 0) <
                Number((row as any).deposit_amount || 0) && (
                <Button
                  size="small"
                  style={{
                    backgroundColor: "#fa8c16",
                    color: "#fff",
                    borderColor: "#fa8c16",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleCollectDeposit!(row);
                  }}
                >
                  Collect Deposit
                </Button>
              )}
            {row.status !== "cancelled" &&
              row.status !== "checked_out" &&
              row.status !== "blocked" &&
              row.status !== "no_show" && (
                <Button
                  size="small"
                  style={isAllocated ? { backgroundColor: '#52c41a', color: '#fff', borderColor: '#52c41a' } : {}}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleOpenAllocate(row);
                  }}
                >
                  {isAllocated ? "Allocated" : "Allocate"}
                </Button>
              )}
            {isAllocated &&
              (row.status === "confirmed" ||
                row.status === "pending" ||
                row.status === "reserved") && (
                <Button
                  size="small"
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleOpenCheckIn(row);
                  }}
                >
                  Check-In
                </Button>
              )}
            {handlers.handleNoShow &&
              (row.status === "pending" ||
                row.status === "confirmed" ||
                row.status === "reserved") && (
                <Button
                  size="small"
                  danger
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleNoShow!(row);
                  }}
                >
                  No-Show
                </Button>
              )}
            {isAllocated && row.status === "checked_in" && (
              <>
                {handlers.handleChangeRoom && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlers.handleChangeRoom!(row);
                    }}
                  >
                    Room
                  </Button>
                )}
                {handlers.handleExtendStay && (
                  <Button
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handlers.handleExtendStay!(row);
                    }}
                  >
                    Extend
                  </Button>
                )}
                <Button
                  size="small"
                  danger
                  type="primary"
                  onClick={(e) => {
                    e.stopPropagation();
                    handlers.handleOpenCheckOut(row);
                  }}
                >
                  Check-Out
                </Button>
              </>
            )}
          </Space>
        </Space>
      );
    },
  },
];

export const getGuestColumns = () => [
  { title: "Name", dataIndex: "full_name", key: "full_name", width: 180 },
  { title: "Email", dataIndex: "email", key: "email", width: 220 },
  { title: "Phone", dataIndex: "phone", key: "phone", width: 130 },
  {
    title: "ID Type",
    dataIndex: "id_document_type",
    key: "id_document_type",
    width: 110,
  },
  {
    title: "ID Number",
    dataIndex: "id_document_number",
    key: "id_document_number",
    width: 160,
  },
  {
    title: "Total Stays",
    dataIndex: "total_stays",
    key: "total_stays",
    width: 100,
  },
  {
    title: "Last Stay",
    dataIndex: "last_stay_date",
    key: "last_stay_date",
    width: 130,
    render: (val: string) => (val ? dayjs(val).format("MMM D, YYYY") : "N/A"),
  },
];
